import StrengthScore from "./StrengthScore";

const BulletList = ({ items, bulletClass }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex gap-2 text-xs text-text-primary leading-snug">
        <span className={`mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full ${bulletClass}`} />
        {item}
      </li>
    ))}
  </ul>
);

const EmptyCell = ({ label }) => (
  <span className="text-text-secondary italic text-xs">No {label}</span>
);

// Wrap all MappingCards inside <MappingTable> instead of rendering them individually
export const MappingTable = ({ mappings = [] }) => {
  if (!mappings.length) return null;

  return (
    <>
      <div className="hidden md:block rounded-xl border border-border-primary overflow-y-auto">
        <div className="grid grid-cols-[minmax(160px,1.4fr)_minmax(180px,2fr)_minmax(180px,2fr)_80px] bg-background-tertiary border-b border-border-primary">
          {["Competency", "Evidence from Resume", "Identified Gaps", "Score"].map((label, i) => (
            <div
              key={label}
              className={[
                "px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-text-secondary whitespace-nowrap",
                i > 0 ? "border-l border-border-primary" : "",
                i === 3 ? "text-center" : "",
              ].join(" ")}
            >
              {label}
            </div>
          ))}
        </div>

        {mappings.map((mapping, i) => {
          const examples = mapping.concrete_examples?.filter((e) => e?.trim()) ?? [];
          const gaps = mapping.identified_gaps?.filter((g) => g?.trim()) ?? [];
          const isLast = i === mappings.length - 1;

          return (
            <div
              key={mapping.id || mapping.competency || i}
              className={[
                "grid grid-cols-[minmax(160px,1.4fr)_minmax(180px,2fr)_minmax(180px,2fr)_80px] items-stretch bg-background-secondary",
                !isLast ? "border-b border-border-primary" : "",
              ].join(" ")}
            >
              <div className="px-4 py-4 text-xs text-text-primary font-semibold leading-snug">
                {mapping.competency_name}
              </div>

              <div className="px-4 py-4 border-l border-border-primary">
                {examples.length > 0 ? (
                  <BulletList items={examples} bulletClass="bg-green-400" />
                ) : (
                  <EmptyCell label="evidence recorded" />
                )}
              </div>

              <div className="px-4 py-4 border-l border-border-primary">
                {gaps.length > 0 ? (
                  <BulletList items={gaps} bulletClass="bg-amber-400" />
                ) : (
                  <EmptyCell label="gaps identified" />
                )}
              </div>

              <div className="px-4 py-4 border-l border-border-primary flex items-start justify-center">
                <StrengthScore score={mapping.strength_score ?? 0} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="md:hidden space-y-2">
        {mappings.map((mapping, i) => {
          const examples = mapping.concrete_examples?.filter((e) => e?.trim()) ?? [];
          const gaps = mapping.identified_gaps?.filter((g) => g?.trim()) ?? [];

          return (
            <div
              key={mapping.id || mapping.competency || i}
              className="rounded-xl border border-border-primary overflow-hidden bg-background-secondary"
            >
              <div className="flex items-start justify-between gap-3 px-4 py-3 bg-background-tertiary border-b border-border-primary">
                <p className="text-xs font-semibold text-text-primary leading-snug flex-1">
                  {mapping.competency_name}
                </p>
                <StrengthScore score={mapping.strength_score ?? 0} />
              </div>

              <div className="px-4 py-3 border-b border-border-primary">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary mb-1.5">
                  Evidence from Resume
                </p>
                {examples.length > 0 ? (
                  <BulletList items={examples} bulletClass="bg-green-400" />
                ) : (
                  <EmptyCell label="evidence recorded" />
                )}
              </div>

              <div className="px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary mb-1.5">
                  Identified Gaps
                </p>
                {gaps.length > 0 ? (
                  <BulletList items={gaps} bulletClass="bg-amber-400" />
                ) : (
                  <EmptyCell label="gaps identified" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// Keep default export for any direct single-card usage
const MappingCard = ({ mapping }) => <MappingTable mappings={mapping ? [mapping] : []} />;
export default MappingCard;